import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Helmet } from 'react-helmet';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Alert
} from 'reactstrap';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { post } from '../hooks/apiFetch';
import { PasswordInput, PasswordResetModal } from '../../helpers';
import './Auth.css';

export const Auth = () => {
    // Контекст и роутинг
    const { isAuthenticated, refreshUser } = useContext(AuthContext);
    const history = useHistory();
    const location = useLocation();

    // === Состояния ===
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [stage, setStage] = useState('credentials'); // 'credentials' | 'twoFactor'
    const [twoFactorCode, setTwoFactorCode] = useState(['', '', '', '', '', '']);
    const [twoFactorEmail, setTwoFactorEmail] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isResending, setIsResending] = useState(false);

    // === Рефы ===
    const inputRefs = useRef([]);
    const timerInterval = useRef(null);
    const abortControllerRef = useRef(null);
    const isMounted = useRef(true);

    // === Очистка таймера ===
    const clearTimer = useCallback(() => {
        if (timerInterval.current) {
            clearInterval(timerInterval.current);
            timerInterval.current = null;
        }
    }, []);

    // === Запуск таймера обратного отсчёта ===
    const startResendTimer = useCallback(() => {
        clearTimer();
        setResendCooldown(60);
        timerInterval.current = setInterval(() => {
            if (isMounted.current) {
                setResendCooldown(prev => {
                    if (prev <= 1) {
                        clearTimer();
                        return 0;
                    }
                    return prev - 1;
                });
            }
        }, 1000);
    }, [clearTimer]);

    // === Эффект монтирования / размонтирования ===
    useEffect(() => {
        isMounted.current = true;
        abortControllerRef.current = new AbortController();

        return () => {
            isMounted.current = false;
            abortControllerRef.current?.abort();
            clearTimer();
        };
    }, [clearTimer]);

    // === Обработчики ввода ===
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        if (name === 'twoFactorCode') return;
        if (name === 'login') setLogin(value);
        else if (name === 'password') setPassword(value);
        setError(null);
    }, []);

    const handleTwoFactorDigitChange = useCallback((index, e) => {
        const value = e.target.value;
        if (value && !/^\d+$/.test(value)) return;
        const digit = value.slice(-1);

        setTwoFactorCode(prev => {
            const newCode = [...prev];
            newCode[index] = digit;
            return newCode;
        });
        setError(null);

        if (digit && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    }, []);

    const handleTwoFactorKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !twoFactorCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    }, [twoFactorCode]);

    const handleTwoFactorPaste = useCallback((e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

        setTwoFactorCode(prev => {
            const newCode = [...prev];
            digits.forEach((digit, idx) => {
                if (idx < 6) newCode[idx] = digit;
            });
            return newCode;
        });

        const lastFilledIndex = digits.length - 1;
        if (lastFilledIndex >= 0 && lastFilledIndex < 5) {
            inputRefs.current[lastFilledIndex + 1]?.focus();
        }
    }, []);

    const toggleShowPassword = useCallback(() => setShowPassword(prev => !prev), []);
    const openResetModal = useCallback(() => setShowResetModal(true), []);
    const closeResetModal = useCallback(() => setShowResetModal(false), []);
    const backToCredentials = useCallback(() => {
        clearTimer();
        setStage('credentials');
        setTwoFactorCode(['', '', '', '', '', '']);
        setError(null);
        setResendCooldown(0);
        setIsResending(false);
    }, [clearTimer]);

    // === Валидация ===
    const validateCredentialsForm = useCallback(() => {
        if (!login) {
            setError('Введите email');
            return false;
        }
        if (!password) {
            setError('Введите пароль');
            return false;
        }
        return true;
    }, [login, password]);

    const validateTwoFactorForm = useCallback(() => {
        const fullCode = twoFactorCode.join('');
        if (fullCode.length !== 6) {
            setError('Введите все 6 цифр кода');
            return false;
        }
        return true;
    }, [twoFactorCode]);

    // === Отправка кода (общий метод) ===
    const sendTwoFactorCode = useCallback(async (email) => {
        await post(
            '/api/auth/SendCode',
            { login: email.trim(), purpose: 2 },
            { signal: abortControllerRef.current?.signal }
        );
    }, []);

    // === Повторная отправка кода ===
    const resendTwoFactorCode = useCallback(async () => {
        if (resendCooldown > 0 || isResending) return;

        setError(null);
        setIsResending(true);
        setTwoFactorCode(['', '', '', '', '', '']);

        try {
            await sendTwoFactorCode(twoFactorEmail);
            setError({ type: 'success', message: 'Новый код отправлен на вашу почту' });
            setIsResending(false);
            inputRefs.current[0]?.focus();
            startResendTimer();
        } catch (error) {
            setError({ type: 'danger', message: 'Ошибка при отправке кода' });
            setIsResending(false);
        }
    }, [resendCooldown, isResending, twoFactorEmail, sendTwoFactorCode, startResendTimer]);

    // === Обработка ошибок авторизации ===
    const handleAuthError = useCallback((error) => {
        if (!isMounted.current) return;
        if (error.name === 'AbortError') return;

        let errorMessage;
        if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
            errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
        } else {
            errorMessage = error.message || 'Ошибка авторизации. Проверьте данные или попробуйте позже';
        }
        setError({ type: 'danger', message: errorMessage });
        console.error('Ошибка:', error);
    }, []);

    // === Редирект после успешной авторизации ===
    const redirectAfterAuth = useCallback(() => {
        const from = location.state?.from || { pathname: '/' };
        history.push(from); // для v6: navigate(from);
    }, [history, location]);

    // === Обработчик отправки формы с логином ===
    const handleCredentialsSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!validateCredentialsForm()) return;

        setIsLoading(true);
        setError(null);

        try {
            const fp = await FingerprintJS.load();
            const { visitorId } = await fp.get();
            const requestData = {
                login: login.trim(),
                password: password.trim(),
                visitorId
            };

            const requiresTwoFactor = await post(
                '/api/auth/Authentication',
                requestData,
                { signal: abortControllerRef.current?.signal }
            );

            if (!isMounted.current) return;

            if (requiresTwoFactor === false) {
                await refreshUser();
                redirectAfterAuth();
                return;
            }

            await sendTwoFactorCode(login.trim());

            if (isMounted.current) {
                setStage('twoFactor');
                setTwoFactorEmail(login.trim());
                setIsLoading(false);
                setTimeout(() => inputRefs.current[0]?.focus(), 100);
                startResendTimer();
            }
        } catch (error) {
            handleAuthError(error);
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }, [login, password, validateCredentialsForm, refreshUser, redirectAfterAuth, sendTwoFactorCode, handleAuthError, startResendTimer]);

    // === Обработчик отправки двухфакторного кода ===
    const handleTwoFactorSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!validateTwoFactorForm()) return;

        setIsLoading(true);
        setError(null);

        try {
            const fp = await FingerprintJS.load();
            const { visitorId } = await fp.get();
            const requestData = {
                login: twoFactorEmail.trim(),
                code: twoFactorCode.join(''),
                purpose: 2,
                visitorId
            };

            await post(
                '/api/auth/CheckCode',
                requestData,
                { signal: abortControllerRef.current?.signal }
            );

            if (!isMounted.current) return;

            await refreshUser();
            redirectAfterAuth();
        } catch (error) {
            handleAuthError(error);
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }, [twoFactorEmail, twoFactorCode, validateTwoFactorForm, refreshUser, redirectAfterAuth, handleAuthError]);

    // === Вспомогательная функция форматирования времени ===
    const formatTime = useCallback((seconds) => {
        const secs = seconds % 60;
        return `0:${secs < 10 ? '0' : ''}${secs}`;
    }, []);

    // === Рендер формы логина ===
    const renderCredentialsForm = () => (
        <Form onSubmit={handleCredentialsSubmit}>
            {error && <Alert color="danger">{error.message || error}</Alert>}
            <FormGroup>
                <Label for="login">Email</Label>
                <Input
                    className="login-input text-input"
                    type="email"
                    id="login"
                    name="login"
                    placeholder="Введите ваш email"
                    value={login}
                    onChange={handleInputChange}
                    disabled={isLoading}
                />
            </FormGroup>
            <Label for="password">Пароль</Label>
            <PasswordInput
                showPassword={showPassword}
                toggleShowPassword={toggleShowPassword}
                password={password}
                handleInputChange={handleInputChange}
                disabled={isLoading}
            />
            <div className="text-left mb-2 mt-1">
                <Button className="reset-pswd" color="link" onClick={openResetModal} disabled={isLoading}>
                    Забыли пароль?
                </Button>
            </div>

            <PasswordResetModal isOpen={showResetModal} onClose={closeResetModal} />

            <div className="text-center">
                {isLoading ? (
                    <Button className="dt1520-button auth-button default-button" outline color="secondary" disabled>
                        <Spinner as="span" size="sm" animation="grow" className="loader" />
                        Проверка...
                    </Button>
                ) : (
                    <Button
                        className="dt1520-button default-button active-dt1520-button auth-button"
                        outline
                        type="submit"
                        draggable="false"
                    >
                        Далее
                    </Button>
                )}
            </div>
        </Form>
    );

    // === Рендер двухфакторной формы ===
    const renderTwoFactorForm = () => (
        <Form onSubmit={handleTwoFactorSubmit}>
            <div className="text-center mb-3">
                <Button
                    color="link"
                    onClick={backToCredentials}
                    className="back-button"
                    disabled={isLoading || isResending}
                >
                    ← Назад к вводу данных
                </Button>
            </div>

            {error && <Alert color={error.type || 'danger'}>{error.message}</Alert>}

            <div className="text-center mb-4">
                <p className="mb-1">Код подтверждения отправлен на</p>
                <p className="font-weight-bold">{twoFactorEmail}</p>
            </div>

            <div className="two-factor-container">
                <Label for="twoFactorCode" className="text-center d-block mb-3">
                    Введите код из письма
                </Label>

                <div className="two-factor-input-group">
                    {twoFactorCode.map((digit, index) => (
                        <Input
                            key={index}
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleTwoFactorDigitChange(index, e)}
                            onKeyDown={(e) => handleTwoFactorKeyDown(index, e)}
                            onPaste={index === 0 ? handleTwoFactorPaste : undefined}
                            innerRef={(ref) => (inputRefs.current[index] = ref)}
                            disabled={isLoading || isResending}
                            className="two-factor-input"
                            autoComplete="off"
                        />
                    ))}
                </div>
            </div>

            <div className="text-center mb-3">
                {resendCooldown > 0 ? (
                    <Button color="link" disabled className="resend-link text-muted">
                        Отправить повторно через {formatTime(resendCooldown)}
                    </Button>
                ) : (
                    <Button
                        color="link"
                        onClick={resendTwoFactorCode}
                        disabled={isResending}
                        className="resend-link"
                    >
                        {isResending ? (
                            <>
                                <Spinner size="sm" className="mr-1" />
                                Отправка...
                            </>
                        ) : (
                            'Отправить код повторно'
                        )}
                    </Button>
                )}
            </div>

            <div className="text-center">
                {isLoading ? (
                    <Button className="dt1520-button auth-button default-button" outline color="secondary" disabled>
                        <Spinner as="span" size="sm" animation="grow" className="loader" />
                        Проверка...
                    </Button>
                ) : (
                    <Button
                        className="dt1520-button default-button active-dt1520-button auth-button"
                        outline
                        type="submit"
                        disabled={twoFactorCode.join('').length !== 6 || isResending}
                        draggable="false"
                    >
                        Подтвердить
                    </Button>
                )}
            </div>
        </Form>
    );

    if (isAuthenticated) {
        return <Redirect to="/" />;
    }

    return (
        <Container fluid className="auth-container">
            <Helmet>
                <title>Авторизация</title>
            </Helmet>
            <Row className="auth-row">
                <Col xs="12" sm="8" md="6" lg="5" xl="4" className="auth-col">
                    <Card className="auth-card">
                        <CardHeader className="auth-card-header">
                            <h4 className="mb-0 text-center">
                                {stage === 'credentials' ? 'Авторизация' : 'Двухфакторная аутентификация'}
                            </h4>
                        </CardHeader>
                        <CardBody className="auth-card-body">
                            {stage === 'credentials' && renderCredentialsForm()}
                            {stage === 'twoFactor' && renderTwoFactorForm()}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};